/**
 * Price display (§10): money is integer minor units end to end. The amount
 * is turned into a decimal STRING with integer arithmetic and handed to
 * Intl as a string — no float ever carries a monetary value.
 */

export interface Money {
  readonly amount: number;
  readonly currency: string;
}

/** Integer minor units → exact decimal string ("5670", 2 → "56.70"). */
export function minorToDecimalString(amount: number, fractionDigits: number): string {
  if (!Number.isSafeInteger(amount)) throw new Error("money is integer minor units only");
  if (!Number.isInteger(fractionDigits) || fractionDigits < 0) throw new Error("fractionDigits must be a non-negative integer");
  const negative = amount < 0;
  const digits = BigInt(Math.abs(amount)).toString().padStart(fractionDigits + 1, "0");
  const whole = digits.slice(0, digits.length - fractionDigits);
  const frac = fractionDigits === 0 ? "" : digits.slice(digits.length - fractionDigits);
  return `${negative ? "-" : ""}${whole}${fractionDigits === 0 ? "" : `.${frac}`}`;
}

/** Locale-aware display. The decimal reaches Intl as a string, never a float. */
export function formatMoney(money: Money, locale = "en-US"): string {
  const formatter = new Intl.NumberFormat(locale, { style: "currency", currency: money.currency });
  const fractionDigits = formatter.resolvedOptions().maximumFractionDigits ?? 2;
  const decimal = minorToDecimalString(money.amount, fractionDigits);
  // Intl.NumberFormat accepts decimal strings (ES2023); the cast bridges the lib types.
  return formatter.format(decimal as unknown as number);
}
